import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { caseStudyAPI } from '../../features/case-study/api/casestudyAPI';
import { websiteInterviewAPI } from '../../features/website-interview/api/websiteInterviewAPI';
import './CaseStudy.css';
import './CaseDiscussion.css';

const SCORE_LABELS = {
  evidence_use: 'Evidence use',
  logic_and_causality: 'Logic and causality',
  trade_off_analysis: 'Trade-off analysis',
  feasibility_and_execution: 'Feasibility and execution',
  risk_awareness: 'Risk awareness',
};

const JARGON_RE =
  /isomorphism|subgraph|source move|node coverage|edge coverage|under-developed|graph updates|edges synced|case graph|%\s*isomorphism/i;

const initials = (name) => {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  return parts.slice(0, 2).map((p) => p[0].toUpperCase()).join('');
};

const activationPlain = (raw) => {
  const s = String(raw || '').toLowerCase();
  if (s === 'strong') return 'strong';
  if (s === 'medium') return 'partly used';
  if (s === 'weak') return 'only hinted';
  if (s === 'absent') return 'missing';
  return raw || '';
};

const formatMissedItem = (item) => {
  if (item == null) return '';
  if (typeof item === 'string') return item.replace(/^case graph\s*/i, '').trim();
  const concepts = Array.isArray(item.concepts_absent) ? item.concepts_absent : [];
  const gap = item.gap || item.miss || '';
  if (concepts.length && gap) return `${concepts.join(', ')} — ${gap}`;
  return concepts.join(', ') || gap;
};

const formatHowToFixItem = (item) => {
  if (item == null) return '';
  if (typeof item === 'string') return item;
  const label = item.addresses_gap ? `${item.addresses_gap}: ` : '';
  return `${label}${item.action || ''}`.trim();
};

const renderStructuredFeedback = (data, item) => {
  if (!data) return item?.feedback_text ? <p>{item.feedback_text}</p> : null;
  const scores = data.scores || {};
  const verdict = data.overall_verdict;
  const sentence =
    (typeof verdict === 'object' ? verdict.sentence || verdict.summary : verdict) || '';
  const coverage = data.reasoning_coverage || item?.isomorphism_score || {};
  const outline = (Array.isArray(data.evidence_outline) ? data.evidence_outline : []).filter(
    (row) => row && String(row.evidence_phrase || '').trim()
  );
  return (
    <div className="feedback-structured">
      {sentence && !JARGON_RE.test(sentence) ? (
        <div className="feedback-verdict-block">
          <span className="feedback-kicker">Overall verdict</span>
          <p className="feedback-verdict-text">{sentence}</p>
        </div>
      ) : null}
      {coverage.total != null ? (
        <div className="feedback-subsection">
          <h5 className="feedback-subsection-title">Reasoning coverage</h5>
          <p className="feedback-score-line">
            <span className="feedback-score-value">
              {typeof coverage.total === 'number' ? coverage.total.toFixed(2) : coverage.total}
            </span>
            {coverage.band ? (
              <span className="feedback-score-emdash">
                {' — '}
                {String(coverage.band).charAt(0).toUpperCase() + String(coverage.band).slice(1)}
              </span>
            ) : null}
          </p>
        </div>
      ) : null}
      {outline.length ? (
        <div className="feedback-subsection">
          <h5 className="feedback-subsection-title">Evidence from your answer</h5>
          <ul className="feedback-bullet-list">
            {outline.map((row, i) => (
              <li key={i}>
                <strong>
                  {String(row.graph || '').toUpperCase() === 'LG' || row.graph === 'Knowledge'
                    ? 'Knowledge'
                    : 'Reasoning'}
                  {row.label ? ` · ${row.label}` : ''}
                  {row.activation ? ` (${activationPlain(row.activation)})` : ''}
                </strong>
                {`: “${row.evidence_phrase}”`}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      {Object.keys(SCORE_LABELS).some((key) => scores[key]) ? (
        <div className="feedback-scores-block">
          <span className="feedback-kicker">Scores (0–10)</span>
          {Object.keys(SCORE_LABELS).map((key) => {
            const row = scores[key];
            if (!row) return null;
            const s = typeof row.score === 'number' ? row.score : parseInt(row.score, 10) || 0;
            const detail = row.comment || row.justification || '';
            return (
              <div key={key} className="feedback-score-row">
                <div className="feedback-score-heading">{SCORE_LABELS[key]}</div>
                <p className="feedback-score-line">
                  <span className="feedback-score-value">{s}/10</span>
                  {detail && !JARGON_RE.test(detail) ? (
                    <>
                      <span className="feedback-score-emdash"> — </span>
                      <span>{detail}</span>
                    </>
                  ) : null}
                </p>
              </div>
            );
          })}
        </div>
      ) : null}
      {Array.isArray(data.major_flaws) && data.major_flaws.length ? (
        <div className="feedback-subsection">
          <h5 className="feedback-subsection-title">Major flaws</h5>
          <ul className="feedback-bullet-list">
            {data.major_flaws.filter((row) => !JARGON_RE.test(String(row))).map((row, i) => (
              <li key={i}>{typeof row === 'string' ? row : formatMissedItem(row)}</li>
            ))}
          </ul>
        </div>
      ) : null}
      {Array.isArray(data.missed_from_case) && data.missed_from_case.length ? (
        <div className="feedback-subsection">
          <h5 className="feedback-subsection-title">What you missed from the case</h5>
          <ul className="feedback-bullet-list">
            {data.missed_from_case.map((row, i) => (
              <li key={i}>{formatMissedItem(row)}</li>
            ))}
          </ul>
        </div>
      ) : null}
      {Array.isArray(data.how_to_fix) && data.how_to_fix.length ? (
        <div className="feedback-subsection">
          <h5 className="feedback-subsection-title">How to fix your answer</h5>
          <ul className="feedback-bullet-list">
            {data.how_to_fix.map((row, i) => (
              <li key={i}>{formatHowToFixItem(row)}</li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
};

/**
 * Harvard discussion panel — same APIs/graph writes as the former full page.
 * Section navigation is owned by CaseStudy when embedded.
 */
const CaseDiscussionPanel = ({
  caseStudyId,
  discussionPreload,
  userName,
  onBackToTutorial,
  onBackToCase,
}) => {
  const navigate = useNavigate();
  const id = caseStudyId;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [script, setScript] = useState(null);
  const [attemptId, setAttemptId] = useState(null);
  const [phase, setPhase] = useState('lobby');
  const [index, setIndex] = useState(0);
  const [speakingId, setSpeakingId] = useState(null);
  const [answer, setAnswer] = useState('');
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [feedbackResults, setFeedbackResults] = useState([]);
  const [status, setStatus] = useState('');
  const [spokenCaption, setSpokenCaption] = useState('');
  const [spokenSpeaker, setSpokenSpeaker] = useState('');

  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const micRef = useRef(null);
  const audioRef = useRef(null);
  const audioCtxRef = useRef(null);
  const audioSourceRef = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const cancelledRef = useRef(false);
  const playingRef = useRef(false);
  const turnsRef = useRef([]);
  const indexRef = useRef(0);
  const answeredRef = useRef(new Set());
  const attemptIdRef = useRef(null);
  const lastUserReplyRef = useRef('');
  const pendingExtractRef = useRef(Promise.resolve());

  const turns = script?.turns || [];
  const current = turns[index] || null;

  useEffect(() => {
    if ((phase === 'playing' || phase === 'user') && videoRef.current && streamRef.current) {
      videoRef.current.srcObject = streamRef.current;
      videoRef.current.muted = true;
      videoRef.current.play().catch(() => {});
    }
  }, [phase]);

  const stopPlayback = () => {
    try {
      if (audioSourceRef.current) {
        try { audioSourceRef.current.stop(); } catch { /* ignore */ }
        audioSourceRef.current = null;
      }
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.src = '';
        audioRef.current = null;
      }
      if (typeof window !== 'undefined' && window.speechSynthesis) {
        window.speechSynthesis.cancel();
      }
    } catch {
      /* ignore */
    }
  };

  const stopMic = () => {
    try {
      if (micRef.current) {
        micRef.current.getTracks().forEach((t) => t.stop());
        micRef.current = null;
      }
    } catch {
      /* ignore */
    }
  };

  const stopMedia = () => {
    stopMic();
    try {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      }
    } catch {
      /* ignore */
    }
  };

  useEffect(() => {
    let cancelled = false;
    const boot = async () => {
      try {
        setLoading(true);
        const usePreload = discussionPreload && discussionPreload.caseStudyId === id;
        const payload = usePreload ? await discussionPreload.promise : await caseStudyAPI.getCaseDiscussion(id);
        if (cancelled) return;
        setScript(payload);
        turnsRef.current = payload.turns || [];
      } catch (err) {
        if (!cancelled) {
          if (err?.status === 401) {
            navigate('/login', {
              state: { from: { pathname: `/case-study/${id}`, search: '?focus=discussion' } },
              replace: true,
            });
            return;
          }
          setError(err?.data?.error || err?.message || 'Failed to load discussion');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    boot();
    return () => {
      cancelled = true;
      cancelledRef.current = true;
      stopPlayback();
      stopMedia();
    };
  }, [id, navigate, discussionPreload]);

  const goTo = (i) => {
    indexRef.current = i;
    setIndex(i);
  };

  const unlockAudio = async () => {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return;
    if (!audioCtxRef.current) audioCtxRef.current = new AC();
    if (audioCtxRef.current.state === 'suspended') {
      await audioCtxRef.current.resume().catch(() => {});
    }
  };

  const playBase64 = async (b64) => {
    await unlockAudio();
    stopPlayback();
    const ctx = audioCtxRef.current;
    if (ctx) {
      try {
        const raw = atob(b64);
        const bytes = new Uint8Array(raw.length);
        for (let i = 0; i < raw.length; i += 1) bytes[i] = raw.charCodeAt(i);
        const audioBuffer = await ctx.decodeAudioData(bytes.buffer.slice(0));
        await new Promise((resolve) => {
          const source = ctx.createBufferSource();
          source.buffer = audioBuffer;
          source.connect(ctx.destination);
          audioSourceRef.current = source;
          source.onended = resolve;
          try {
            source.start();
          } catch {
            resolve();
          }
        });
        return;
      } catch {
        /* fall through to element playback */
      }
    }
    await new Promise((resolve) => {
      const audio = new Audio(`data:audio/mpeg;base64,${b64}`);
      audioRef.current = audio;
      audio.onended = resolve;
      audio.onerror = resolve;
      audio.play().catch(() => resolve());
    });
  };

  const speakBrowser = (text) =>
    new Promise((resolve) => {
      if (!('speechSynthesis' in window)) {
        setTimeout(resolve, 1600);
        return;
      }
      window.speechSynthesis.cancel();
      const utter = new SpeechSynthesisUtterance(text);
      utter.onend = resolve;
      utter.onerror = resolve;
      window.speechSynthesis.speak(utter);
    });

  const speakText = async (text, voice) => {
    const spoken = (text || '').trim();
    if (!spoken || cancelledRef.current) return;
    setSpokenCaption(spoken);
    try {
      const result = await caseStudyAPI.generateDiscussionSpeech(spoken, voice);
      if (result?.audio_data && !cancelledRef.current) {
        await playBase64(result.audio_data);
        return;
      }
    } catch {
      /* browser fallback below */
    }
    await speakBrowser(spoken);
  };

  const speakTurn = async (turn) => {
    if (!turn?.text || cancelledRef.current) return;
    setSpeakingId(turn.id);
    setSpokenSpeaker(turn.speaker || 'Facilitator');
    setSpokenCaption('…');
    setStatus(`${turn.speaker} is speaking…`);
    await unlockAudio();
    let spoken = turn.text;
    const aid = attemptIdRef.current;
    if (aid && turn.role !== 'user' && !turn.alreadyPhrased) {
      try {
        const rendered = await caseStudyAPI.renderDiscussionLine(aid, {
          turn_id: turn.id,
          last_user_reply: lastUserReplyRef.current || undefined,
        });
        if (rendered?.text) spoken = rendered.text;
      } catch {
        spoken = turn.text;
      }
    }
    if (cancelledRef.current) return;
    await speakText(spoken, turn.voice);
  };

  const speakFacilitatorBridge = async (nextSpeaker) => {
    const aid = attemptIdRef.current;
    const invite = (nextSpeaker || '').trim();
    if (!aid || !lastUserReplyRef.current || !invite) return;
    setSpeakingId('facilitator-bridge');
    setSpokenSpeaker('Facilitator');
    setSpokenCaption('…');
    setStatus('The facilitator is responding…');
    await unlockAudio();
    let spoken = `That's a useful point. ${invite}, would you state your view?`;
    try {
      const rendered = await caseStudyAPI.renderDiscussionLine(aid, {
        kind: 'bridge',
        last_user_reply: lastUserReplyRef.current,
        next_speaker: invite,
      });
      if (rendered?.text) spoken = rendered.text;
    } catch {
      /* keep fallback line */
    }
    if (cancelledRef.current) return;
    await speakText(spoken, 'onyx');
  };

  const finishDiscussion = async (activeAttemptId) => {
    const aid = activeAttemptId || attemptIdRef.current || attemptId;
    stopPlayback();
    stopMedia();
    setError(null);
    setPhase('closing');
    setSpeakingId(null);
    setSpokenSpeaker('');
    setStatus('Room closed. Writing your reasoning to the graph…');
    try {
      await pendingExtractRef.current;
    } catch {
      /* leftover extracts retried below */
    }
    try {
      const snapshot = await caseStudyAPI.getCaseDiscussionAttempt(aid).catch(() => null);
      const leftover = snapshot?.graph_pending || [];
      for (let i = 0; i < leftover.length; i += 1) {
        setStatus(`Writing contribution ${i + 1} of ${leftover.length} to the graph…`);
        await caseStudyAPI.extractDiscussionUtterance(aid, leftover[i]).catch(() => null);
      }
      setStatus('Preparing your discussion notes…');
      let completed;
      try {
        completed = await caseStudyAPI.completeCaseDiscussionAttempt(aid);
      } catch (err) {
        const existing = await caseStudyAPI.getCaseDiscussionAttempt(aid).catch(() => null);
        if (existing?.feedback?.length) {
          completed = existing;
        } else {
          throw err;
        }
      }
      setFeedbackResults(completed.feedback || []);
      setPhase('done');
      stopMedia();
    } catch (err) {
      setError(err?.data?.error || err?.message || 'Could not finish notes. Stay on this page and retry.');
    }
  };

  const playFrom = async (startIndex, activeAttemptId) => {
    if (playingRef.current) return;
    playingRef.current = true;
    try {
      let i = startIndex;
      const list = turnsRef.current;
      while (i < list.length && !cancelledRef.current) {
        const turn = list[i];
        const prev = i > 0 ? list[i - 1] : null;
        goTo(i);
        if (turn.role === 'user') {
          if (answeredRef.current.has(turn.id)) {
            i += 1;
            continue;
          }
          setSpokenSpeaker('You');
          setSpokenCaption(turn.instruction || `Respond to ${turn.responds_to || 'the last speaker'}.`);
          setPhase('user');
          setStatus('Your turn — click Speak, then Stop when you are done.');
          return;
        }
        setPhase('playing');
        setSpokenSpeaker(prev?.role === 'user' ? 'Facilitator' : (turn.speaker || 'Facilitator'));
        setSpokenCaption('…');
        if (prev?.role === 'user' && turn.role === 'character' && lastUserReplyRef.current) {
          await speakFacilitatorBridge(turn.speaker);
        }
        await speakTurn(turn);
        i += 1;
      }
      if (!cancelledRef.current) {
        await new Promise((resolve) => setTimeout(resolve, 700));
        await finishDiscussion(activeAttemptId);
      }
    } catch (err) {
      setError(err?.data?.error || err?.message || 'Discussion playback failed');
    } finally {
      playingRef.current = false;
      setSpeakingId(null);
    }
  };

  const handleJoin = async () => {
    setError(null);
    cancelledRef.current = false;
    answeredRef.current = new Set();
    lastUserReplyRef.current = '';
    pendingExtractRef.current = Promise.resolve();
    try {
      await unlockAudio();
      let stream;
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: 'user' },
          audio: true,
        });
      } catch {
        stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      }
      streamRef.current = stream;
      const attempt = await caseStudyAPI.startCaseDiscussionAttempt(id);
      attemptIdRef.current = attempt.attempt_id;
      setAttemptId(attempt.attempt_id);
      setPhase('playing');
      await playFrom(0, attempt.attempt_id);
    } catch (err) {
      stopMedia();
      setError(
        err?.data?.error ||
          err?.message ||
          'Camera and microphone are needed for the discussion.'
      );
    }
  };

  const pickRecorderMime = () => {
    if (typeof MediaRecorder === 'undefined') return '';
    const candidates = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg'];
    return candidates.find((type) => MediaRecorder.isTypeSupported(type)) || '';
  };

  const startRecording = async () => {
    if (recording || submitting || transcribing) return;
    if (typeof MediaRecorder === 'undefined') {
      setError('This browser cannot record audio. Type your reply instead.');
      return;
    }
    setError(null);
    try {
      await unlockAudio();
      stopMic();
      const mic = await navigator.mediaDevices.getUserMedia({ audio: true });
      micRef.current = mic;
      chunksRef.current = [];
      const mime = pickRecorderMime();
      const recorder = mime
        ? new MediaRecorder(mic, { mimeType: mime })
        : new MediaRecorder(mic);
      recorderRef.current = recorder;
      recorder.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
      };
      recorder.start(250);
      setRecording(true);
      setStatus('Listening… speak now, then click Stop.');
    } catch (err) {
      stopMic();
      setError(err?.message || 'Microphone did not start. Allow mic access and try Speak again.');
    }
  };

  const stopRecordingAndTranscribe = async () => {
    if (!recorderRef.current || !recording) return;
    setRecording(false);
    setTranscribing(true);
    setStatus('Transcribing your words…');
    try {
      const recorder = recorderRef.current;
      const mime = recorder.mimeType || 'audio/webm';
      const blob = await new Promise((resolve, reject) => {
        recorder.onstop = () => resolve(new Blob(chunksRef.current, { type: mime }));
        recorder.onerror = reject;
        try {
          recorder.stop();
        } catch (err) {
          reject(err);
        }
      });
      stopMic();
      if (!blob.size) {
        setError('No audio was captured. Click Speak and talk, then Stop.');
        return;
      }
      const result = await websiteInterviewAPI.transcribeAudioBlob(blob);
      const text = (result?.text || result?.transcript || '').trim();
      if (text) setAnswer((prev) => (prev ? `${prev.trim()} ${text}` : text));
      else setError(result?.error || 'Could not hear that. Try Speak again, or type your reply.');
    } catch (err) {
      setError(err?.message || 'Transcription failed. You can type your reply instead.');
    } finally {
      setTranscribing(false);
      recorderRef.current = null;
      chunksRef.current = [];
      setStatus('Review your words, then send.');
    }
  };

  const handleSubmitTurn = async () => {
    const currentIndex = indexRef.current;
    const turn = turnsRef.current[currentIndex];
    const aid = attemptIdRef.current || attemptId;
    if (!turn || turn.role !== 'user' || !aid || submitting) return;
    const trimmed = answer.trim();
    if (trimmed.length < 20) {
      setError('Please give a fuller response (a few sentences).');
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      const saved = await caseStudyAPI.submitCaseDiscussionUtterance(aid, {
        turn_id: turn.id,
        utterance_text: trimmed,
      });
      answeredRef.current.add(turn.id);
      lastUserReplyRef.current = trimmed;
      setAnswer('');
      if (saved?.utterance_id) {
        pendingExtractRef.current = Promise.all([
          pendingExtractRef.current,
          caseStudyAPI.extractDiscussionUtterance(aid, saved.utterance_id).catch(() => null),
        ]);
      }
      await unlockAudio();
      await playFrom(currentIndex + 1, aid);
    } catch (err) {
      const msg = err?.data?.error || err?.message || 'Failed to submit response';
      const already = /already answered/i.test(String(msg));
      if (already) {
        answeredRef.current.add(turn.id);
        setAnswer('');
        await playFrom(currentIndex + 1, aid);
        return;
      }
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleBackToCase = () => {
    if (typeof onBackToCase === 'function') {
      onBackToCase();
      return;
    }
    navigate(`/case-study/${id}`);
  };

  const handleBackToTutorial = () => {
    if (typeof onBackToTutorial === 'function') {
      onBackToTutorial();
      return;
    }
    navigate(`/case-study/${id}?focus=tutorial`);
  };

  const participants = [
    { key: 'facilitator', name: 'Facilitator', role: 'facilitator' },
    ...(script?.characters || []).map((c) => ({
      key: c.id,
      name: c.name,
      role: 'character',
    })),
    { key: 'you', name: 'You', role: 'user' },
  ];

  const activeKey = (() => {
    if (spokenSpeaker === 'Facilitator' || (!spokenSpeaker && current?.role === 'facilitator')) {
      return 'facilitator';
    }
    if (spokenSpeaker === 'You' || (!spokenSpeaker && current?.role === 'user')) {
      return 'you';
    }
    const name = spokenSpeaker || current?.speaker;
    const match = (script?.characters || []).find((c) => c.name === name);
    return match?.id || name || null;
  })();

  if (loading) {
    return (
      <section className="decision-section-animated case-assessment-section natively-scrollable">
        <div className="case-study-loading">Loading Harvard discussion…</div>
      </section>
    );
  }

  if (error && !script) {
    return (
      <section className="decision-section-animated case-assessment-section natively-scrollable">
        <div className="case-study-error">
          <h2>Could not load discussion</h2>
          <p>{error}</p>
          <button type="button" className="decision-submit-btn" onClick={handleBackToCase}>
            Back to case
          </button>
        </div>
      </section>
    );
  }

  return (
    <section className="decision-section-animated case-assessment-section discussion-embedded natively-scrollable" data-tour="case-discussion">
      <div className="discussion-shell discussion-shell--embedded natively-scrollable">
        {phase === 'done' ? (
          <div className="feedback-section">
            <div className="decision-header-block">
              <div className="section-eyebrow">Harvard Case Method</div>
              <p className="case-assessment-kicker">Harvard case discussion</p>
              <h2 className="decision-main-title">Discussion complete</h2>
              <div className="section-divider" />
              <p className="decision-main-subtitle">
                Your contributions were marked against the case reasoning — whether you answered
                each speaker with a clear step and the facts that support it.
              </p>
            </div>
            {feedbackResults.map((item) => (
              <div key={item.question_id || item.rank} className="feedback-content" style={{ marginBottom: 28 }}>
                <p style={{ opacity: 0.7, marginBottom: 12 }}>
                  Contribution {item.rank}
                  {item.responds_to ? ` · reply to ${item.responds_to}` : ''}
                </p>
                {renderStructuredFeedback(item.feedback_structured, item)}
              </div>
            ))}
            <div className="feedback-actions">
              <button type="button" className="decision-submit-btn" onClick={handleBackToCase}>
                Back to start of case
              </button>
              <button type="button" className="try-again-btn" onClick={handleBackToTutorial}>
                Back to Oxford Tutorial
              </button>
            </div>
          </div>
        ) : phase === 'closing' ? (
          <div className="discussion-lobby">
            <div className="section-eyebrow">Harvard Case Method</div>
            <h2 className="decision-main-title">The room is closed</h2>
            <div className="section-divider" />
            <p className="decision-main-subtitle">
              Saving your replies onto the case reasoning graph — this is the student-side
              record of what you used, not a change to the case itself.
            </p>
            {error ? null : <div className="discussion-closing-mark" aria-hidden="true" />}
            <p className="discussion-status">{status || 'Writing your graph…'}</p>
            {error ? (
              <>
                <p className="discussion-error">{error}</p>
                <button type="button" className="decision-submit-btn" onClick={() => finishDiscussion(attemptId)}>
                  Retry graph save
                </button>
              </>
            ) : null}
          </div>
        ) : phase === 'lobby' ? (
          <div className="discussion-lobby">
            <div className="section-eyebrow">Harvard Case Method</div>
            <h2 className="decision-main-title">Join the discussion</h2>
            <div className="section-divider" />
            <p className="decision-main-subtitle">
              The facilitator will open the room. Each participant states a fixed position.
              After each one, you answer — on camera, out loud — as you would in class.
            </p>
            <div className="discussion-cast">
              <span className="discussion-chip">Facilitator</span>
              {(script?.characters || []).map((c) => (
                <span key={c.id} className="discussion-chip">{c.name}</span>
              ))}
              <span className="discussion-chip">{userName || 'You'}</span>
            </div>
            <button type="button" className="decision-submit-btn decision-submit-btn--compact" onClick={handleJoin}>
              Join Discussion
            </button>
            {error ? <p className="discussion-error">{error}</p> : null}
          </div>
        ) : (
          <div className="discussion-room">
            <div className="discussion-stage">
              <div className="discussion-participants">
                {participants.map((p) => (
                  <div
                    key={p.key}
                    className={[
                      'discussion-avatar',
                      activeKey === p.key ? 'is-active' : '',
                      p.role === 'user' ? 'is-you' : '',
                      speakingId && activeKey === p.key ? 'is-speaking' : '',
                    ].filter(Boolean).join(' ')}
                  >
                    <div className="discussion-face">{initials(p.name)}</div>
                    <div className="discussion-avatar-name">{p.name}</div>
                    <div className="discussion-role">
                      {p.role === 'facilitator' ? 'Moderator' : p.role === 'user' ? 'You' : 'Participant'}
                    </div>
                  </div>
                ))}
              </div>
              <div className="discussion-caption">
                <strong>{spokenSpeaker || current?.speaker || 'Facilitator'}</strong>
                {current?.role === 'user' && spokenSpeaker !== 'Facilitator'
                  ? (current.instruction || `Respond to ${current.responds_to || 'the last speaker'}.`)
                  : (spokenCaption || current?.text || 'The room is gathering…')}
              </div>
            </div>

            <aside className="discussion-side">
              <div className="discussion-video-wrap">
                <video ref={videoRef} autoPlay muted playsInline />
                {recording ? (
                  <div className="discussion-live-badge">
                    <span className="discussion-live-dot" />
                    Listening
                  </div>
                ) : null}
              </div>
              <h3>{phase === 'user' ? 'Your turn' : 'In session'}</h3>
              <p>
                {phase === 'user'
                  ? `Answer ${current?.responds_to || 'the last speaker'} directly. Agree, push back, and say what you would do.`
                  : 'Stay on camera. The facilitator is running the discussion.'}
              </p>
              {phase === 'user' ? (
                <>
                  <div className="discussion-controls">
                    {!recording ? (
                      <button
                        type="button"
                        className="try-again-btn"
                        onClick={startRecording}
                        disabled={submitting || transcribing}
                      >
                        {transcribing ? 'Transcribing…' : 'Speak'}
                      </button>
                    ) : (
                      <button type="button" className="decision-submit-btn" onClick={stopRecordingAndTranscribe}>
                        Stop &amp; transcribe
                      </button>
                    )}
                    <button
                      type="button"
                      className="decision-submit-btn"
                      onClick={handleSubmitTurn}
                      disabled={submitting || recording || transcribing || answer.trim().length < 20}
                    >
                      {submitting ? 'Saving your contribution…' : 'Send to the room'}
                    </button>
                  </div>
                  <textarea
                    className="discussion-textarea"
                    value={answer}
                    onChange={(e) => setAnswer(e.target.value)}
                    disabled={submitting || recording}
                    placeholder="Speak, or type if you need to. Feedback comes after the closing."
                  />
                </>
              ) : null}
              {status ? <p className="discussion-status">{status}</p> : null}
              {error ? <p className="discussion-error">{error}</p> : null}
            </aside>
          </div>
        )}
      </div>
    </section>
  );
};

export default CaseDiscussionPanel;
